import TwinTableIconColumn from './twinTableIconColumn.vue';
import NiotaClassicCommonTablePageSelector from '@/components/niotaClassic/common/tablePageSelector.vue';
import get from 'lodash/get';
import debounce from 'lodash/debounce';
import twinFields from '../twinFields';
import { mapState } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import { createNestedDashboardParameters } from '@/utils/dashboard-utils';
import api from '@/services/apiService/dashboards';
const {
  getDefinitions: getTwinFields
} = twinFields;
export default {
  name: 'TwinTable',
  components: {
    TwinTableIconColumn,
    NiotaClassicCommonTablePageSelector
  },
  props: {
    widget: {
      type: Object,
      required: true
    },
    dashboardId: {
      type: String,
      required: false,
      default: undefined
    },
    data: {
      type: Object,
      default: () => ({})
    },
    loading: {
      type: Boolean,
      required: false,
      default: false
    },
    dateRange: {
      type: Object,
      required: true
    },
    editMode: {
      type: Boolean,
      required: true
    },
    customParameters: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  data() {
    return {
      // user search term (debounced before it triggers a backend query)
      searchTerm: '',
      // current page of rows returned by the backend
      serverRows: [],
      // total number of rows matching the current search/filter (for pagination)
      serverTotalCount: 0,
      serverLoading: false,
      // synced with v-data-table: page / itemsPerPage / sort
      tableOptionsState: {
        page: 1,
        itemsPerPage: (this.widget.twinTableOptions || {}).rowsPerPage || 10,
        sortBy: [],
        sortDesc: []
      }
    };
  },
  computed: {
    ...mapState('dashboards', ['dashboards']),
    ...mapState('app', ['color', 'inpOutlined', 'inpFilled']),
    isLoading() {
      return this.serverLoading;
    },
    serverItemsLength() {
      return this.serverTotalCount;
    },
    dataConfigKey() {
      return JSON.stringify({
        twinFilter: this.widget.twinFilter,
        virtualDeviceFilter: this.widget.virtualDeviceFilter,
        twinTableOptions: this.widget.twinTableOptions
      });
    },
    completeWidget() {
      const clone = cloneDeep(this.widget);
      if (!clone.twinTableOptions) {
        clone.twinTableOptions = {
          columns: []
        };
      }
      if (!clone.twinTableOptions.columns) {
        clone.twinTableOptions.columns = [];
      }
      if (clone.nestedDashboard) {
        if (!clone.nestedDashboard.parameters) {
          clone.nestedDashboard.parameters = {};
        }
      }
      return clone;
    },
    twinFields() {
      return getTwinFields(this.$t, this.twins);
    },
    twins() {
      return this.serverRows;
    },
    columns() {
      return Array.isArray(this.completeWidget.twinTableOptions.columns) ? this.completeWidget.twinTableOptions.columns : [];
    },
    items() {
      return this.twins.map(twin => {
        const item = Object.fromEntries(this.columns.map((column, columnIndex) => {
          const fieldDefinition = get(this.twinFields, `['${column.property}']`);
          let value = '';
          if (column.property.startsWith('state.')) {
            const stateKey = column.property.replace('state.', '');

            // Get value from history.histories array (aggregated historical data)
            const histories = get(twin, `_states['${stateKey}'].history.histories`, []);
            if (histories.length > 0) {
              const sortedHistories = [...histories].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
              // Find the latest non-null, non-empty value
              const latestNonNullHistoryItem = sortedHistories.find(item => item.value !== null && item.value !== undefined && item.value !== '');
              value = get(latestNonNullHistoryItem, 'value', '');
            }
          } else if (fieldDefinition) {
            value = get(twin, fieldDefinition.key);
          } else {
            console.warn(`No field definition found for property: ${column.property}`);
            value = column.property;
          }
          if (typeof get(fieldDefinition, 'transformer') === 'function') {
            value = fieldDefinition.transformer(value);
          }
          return [`${columnIndex}-${column.property}`, value];
        }));
        item.__twin_id__ = twin.id;
        return item;
      });
    },
    headers() {
      return this.columns.map((column, columnIndex) => {
        const fieldDefinition = get(this.twinFields, `['${column.property}']`);
        let label = column.label;
        if (column.property.startsWith('state.')) {
          const stateKey = column.property.replace('state.', '');
          label = label || stateKey;
        } else if (fieldDefinition) {
          label = label || fieldDefinition.defaultLabel;
        }
        let width = column.width || undefined;
        if (!Number.isNaN(Number(width))) {
          width = `${width}px`;
        }
        return {
          text: label,
          value: `${columnIndex}-${column.property}`,
          component: get(fieldDefinition, 'component', 'string'),
          // only Postgres-backed columns can be sorted server-side; state/keyValue/etc. are not
          sortable: column.sortOrder !== 'disabled' && this.isServerSortableProperty(column.property),
          defaultSortOrder: column.sortOrder,
          width,
          dataCy: `table-col-${columnIndex}-${column.property}`
        };
      });
    },
    itemsPerPageOptions() {
      return (
        // mirror the app-wide convention (see virtualDevice/list.vue tableFooterProps)
        [10, 20, 50, 100, 200, this.completeWidget.twinTableOptions.rowsPerPage]
        // drop unset / non-numeric page sizes (e.g. an unconfigured rowsPerPage would add `undefined`)
        .filter(option => Number.isFinite(option) && option > 0).filter((option, index, self) => self.indexOf(option) === index).sort((a, b) => a - b)
      );
    }
  },
  watch: {
    'completeWidget.nestedDashboard.dashboardId': {
      handler(nestedDashboardId) {
        if (!nestedDashboardId) {
          return;
        }
        this.$store.dispatch('dashboards/refreshOne', {
          dashboardId: nestedDashboardId
        });
      },
      immediate: true
    },
    searchTerm() {
      // a new search always restarts at the first page
      this.tableOptionsState.page = 1;
      this.reloadDebounced();
    },
    tableOptionsState: {
      deep: true,
      handler() {
        this.reloadDebounced();
      }
    },
    dateRange: {
      deep: true,
      handler() {
        this.reloadDebounced();
      }
    },
    customParameters: {
      deep: true,
      handler() {
        this.reloadDebounced();
      }
    },
    dataConfigKey() {
      this.reloadDebounced();
    }
  },
  created() {
    // initialise sort from the first column with a configured default sort order.
    // The table is single-sort (see :multi-sort="false") and the backend sorts by one column,
    // so we only ever seed one sort key.
    const defaultSortColumnIndex = this.columns.findIndex(column => column.sortOrder && column.sortOrder !== 'disabled');
    if (defaultSortColumnIndex !== -1) {
      const column = this.columns[defaultSortColumnIndex];
      this.tableOptionsState.sortBy = [`${defaultSortColumnIndex}-${column.property}`];
      this.tableOptionsState.sortDesc = [column.sortOrder === 'desc'];
    }

    // debounced so rapid keystrokes / option changes collapse into a single backend query
    this.reloadDebounced = debounce(this.reload, 350);
  },
  mounted() {
    this.reload();
  },
  beforeDestroy() {
    // avoid a pending debounced reload firing against a torn-down instance
    if (this.reloadDebounced && typeof this.reloadDebounced.cancel === 'function') {
      this.reloadDebounced.cancel();
    }
  },
  methods: {
    // jump to a page (the selector already clamps); updating page triggers the debounced reload
    setTablePage(value) {
      this.tableOptionsState.page = value;
    },
    // every digitaltwins-backed column is server-sortable; state values (InfluxDB, fetched per page)
    // and dashboard parameters (constant per row) are not (mirrors mapTableColumnToOrderBy)
    isServerSortableProperty(property) {
      return !property.startsWith('state.') && !property.startsWith('dashboard.parameters.');
    },
    // maps the active v-data-table sort column to a backend orderBy ({ property, direction }).
    // Only Postgres-backed columns are sortable server-side; the backend defaults the rest to title.
    buildOrderBy() {
      const {
        sortBy,
        sortDesc
      } = this.tableOptionsState;
      if (!sortBy || !sortBy.length) {
        return undefined;
      }
      const columnIndex = Number(`${sortBy[0]}`.split('-')[0]);
      const column = this.columns[columnIndex];
      if (!column) {
        return undefined;
      }
      return {
        property: column.property,
        direction: sortDesc && sortDesc[0] ? 'desc' : 'asc'
      };
    },
    async reload() {
      // no dashboard context (e.g. some preview cases) — fall back to any prefetched data
      if (!this.dashboardId) {
        this.serverRows = this.data && this.data['twin-table'] || [];
        this.serverTotalCount = this.serverRows.length;
        return;
      }
      this.serverLoading = true;
      try {
        const itemsPerPage = this.tableOptionsState.itemsPerPage > 0 ? this.tableOptionsState.itemsPerPage : this.completeWidget.twinTableOptions.rowsPerPage || 10;
        const page = this.tableOptionsState.page || 1;
        const data = await api.fetchTwinTableData(this.dashboardId, this.completeWidget, {
          dateRange: this.dateRange,
          customParameters: this.customParameters,
          search: this.searchTerm || '',
          start: (page - 1) * itemsPerPage,
          limit: itemsPerPage,
          orderBy: this.buildOrderBy()
        });
        this.serverRows = data['twin-table'] || [];
        this.serverTotalCount = Number.isFinite(data.totalCount) ? data.totalCount : this.serverRows.length;
      } catch (error) {
        console.error('twin-table: failed to load data', error);
        this.serverRows = [];
        this.serverTotalCount = 0;
      } finally {
        this.serverLoading = false;
      }
    },
    getByKey(obj, key) {
      return get(obj, key);
    },
    rowHasClickHandler(_item) {
      if (!_item) {
        return {
          isTrue: false
        };
      }
      if (this.editMode) {
        return {
          isTrue: false
        };
      }
      const nestedDashboardId = get(this.completeWidget, 'nestedDashboard.dashboardId');
      if (!nestedDashboardId) {
        return {
          isTrue: false
        };
      }
      const nestedDashboard = this.dashboards.find(dashboard => dashboard.id === nestedDashboardId);
      if (!nestedDashboard) {
        console.error(`Nested dashboard with id ${nestedDashboardId} not found`);
        this.$snack(this.$t('Dashboards.components.twinTable.error.nestedDashboardNotFound'), 'error');
        return {
          isTrue: false
        };
      }
      return {
        isTrue: true,
        data: {
          nestedDashboard
        }
      };
    },
    onRowClick(item) {
      const {
        isTrue: hasRowClickHandler,
        data
      } = this.rowHasClickHandler(item);
      if (!hasRowClickHandler) {
        return;
      }
      const twin = this.twins.find(twin => twin.id === item.__twin_id__);
      const nestedDashboard = data.nestedDashboard;
      const nestedDashboardParameters = createNestedDashboardParameters(nestedDashboard, this.completeWidget.nestedDashboard.parameters, this.customParameters, twin);
      this.$emit('activate-sub-dashboard', {
        dashboardId: nestedDashboard.id,
        customParameters: nestedDashboardParameters,
        navigationType: this.completeWidget.nestedDashboard.navigationType
      });
    }
  }
};